import { assignPrototypeAPIs, type SortingState, type TableFeature } from "@tanstack/react-table";
import type { MetricLabel } from "../../../modules/merchandising/utils/metrics";

export interface AlternateSortingOptions {
  alternateSortingEnabled?: boolean;
}

export interface AlternateSortingColumn {
  isAltSorted: () => boolean;
  getAltSortKey: () => string;
}

export interface AlternateSortingColumnDef {
  alternateSortingOptions?: { value: string; label: MetricLabel }[];
}

/** Structural: the concrete `Column<AppTableFeatures, …>` would be a circular import. */
interface AltSortColumn {
  id: string;
  columnDef: { meta?: AlternateSortingColumnDef };
  table: { atoms: { sorting: { get: () => SortingState } } };
}

function altSortIds(column: AltSortColumn, includeSelf: boolean): string[] {
  const ids = column.columnDef.meta?.alternateSortingOptions?.map((option) => option.value) ?? [];
  return includeSelf ? [...ids, column.id] : ids;
}

function findAltSort(column: AltSortColumn, includeSelf: boolean) {
  const ids = altSortIds(column, includeSelf);
  return column.table.atoms.sorting.get().find((sort) => ids.includes(sort.id));
}

export const alternateSortingFeature: TableFeature = {
  getDefaultTableOptions: (): AlternateSortingOptions => ({
    // Opt-in per table: the feature is registered globally, the menu is not on by default.
    alternateSortingEnabled: false,
  }),

  assignColumnPrototype: (prototype, table) => {
    assignPrototypeAPIs("alternateSortingFeature", prototype, table, {
      // Overrides the built-in, so this feature must be registered after `rowSortingFeature`.
      column_getIsSorted: {
        fn: (column: AltSortColumn) => {
          const columnSort = findAltSort(column, true);

          return (
            !columnSort ? false
            : columnSort.desc ? "desc"
            : "asc"
          );
        },
      },
      column_isAltSorted: {
        fn: (column: AltSortColumn) => !!findAltSort(column, false),
      },
      column_getAltSortKey: {
        fn: (column: AltSortColumn) => {
          const columnSort = findAltSort(column, true);
          return `${columnSort?.id || ""}-${columnSort?.desc ? "desc" : "asc"}`;
        },
      },
    });
  },
};
